/**
 * Keycloak OIDC client for the Timesheet Compliance SPA.
 *
 * Uses the same Keycloak config as the Flask fulfillment dashboard
 * (auth.rbsprod.net / RANCHOBIOSCIENCES / DASH-LOGIN) so users get
 * single sign-on across both apps.
 */
import Keycloak from "keycloak-js";

const url =
  (import.meta.env.VITE_KEYCLOAK_URL as string | undefined) ??
  "https://auth.rbsprod.net";
const realm =
  (import.meta.env.VITE_KEYCLOAK_REALM as string | undefined) ??
  "RANCHOBIOSCIENCES";
const clientId =
  (import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined) ??
  "DASH-LOGIN";

export const keycloak = new Keycloak({ url, realm, clientId });

export async function initKeycloak(): Promise<boolean> {
  const authenticated = await keycloak.init({
    onLoad: "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false,
    enableLogging: false,
  });
  if (authenticated) startTokenRefresh();
  return authenticated;
}

function startTokenRefresh() {
  setInterval(() => {
    keycloak.updateToken(60).catch(() => keycloak.login());
  }, 4 * 60 * 1000);
}

export function bearerToken(): string | undefined {
  return keycloak.token;
}

export function userDisplayName(): string {
  const t = keycloak.tokenParsed as
    | { name?: string; preferred_username?: string; email?: string }
    | undefined;
  return t?.name ?? t?.preferred_username ?? t?.email ?? "Signed in";
}

export function userEmail(): string | undefined {
  const t = keycloak.tokenParsed as { email?: string } | undefined;
  return t?.email;
}

export function signOut() {
  keycloak.logout({ redirectUri: window.location.origin });
}

/**
 * Keycloak groups/roles allowed to use this dashboard. A valid login
 * is not enough — the token must also prove membership in one of
 * these. Mirrors ALLOWED_GROUPS in the Flask backend's
 * compliance_api.py; keep the two lists in sync.
 *
 * Names are compared after normalization (case-insensitive, hyphens
 * and underscores equivalent), because this realm delivers these as
 * lowercase realm roles like "dash_slt" — see the fulfillment
 * dashboard's ALLOWED_ROLES, the reference implementation.
 */
export const ALLOWED_GROUPS = [
  "DASH-ADMIN",
  // The realm's actual role name is the singular DASH-MANAGER
  // (verified from a live token); DASH-MANAGERS kept as an alias in
  // case a plural group/role is ever added.
  "DASH-MANAGER",
  "DASH-MANAGERS",
  "DASH-PM",
  "DASH-SLT",
  // The realm also has a bare "slt" role (allowed by the fulfillment
  // dashboard); treat it as SLT membership here too. Remove this line
  // for a strictly DASH-SLT check.
  "SLT",
] as const;

type GroupClaims = {
  groups?: string[];
  roles?: string[] | string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
};

/**
 * Normalize a group/role name for comparison: trim, uppercase, and
 * treat underscores/whitespace as hyphens so the realm role
 * "dash_slt" matches the configured "DASH-SLT".
 */
function normalizeName(raw: string): string {
  return raw.trim().replace(/[\s_]+/g, "-").toUpperCase();
}

/**
 * Every group/role name the token carries, normalized for comparison
 * (uppercased, underscores treated as hyphens, group paths like
 * "/DASH-ADMIN" reduced to their last segment). Keycloak setups
 * differ: this realm delivers memberships as realm roles
 * (realm_access.roles: ["dash_slt", ...]), but a `groups` claim,
 * client roles, and a custom top-level `roles` claim are collected
 * too — same fallback chain as the fulfillment dashboard's
 * _get_user_roles().
 */
export function userGroups(): Set<string> {
  const names = new Set<string>();
  const add = (raw: unknown) => {
    if (typeof raw !== "string" || !raw.trim()) return;
    const leaf = raw.trim().replace(/\/+$/, "").split("/").pop() ?? "";
    if (leaf) names.add(normalizeName(leaf));
  };
  const collect = (t: GroupClaims | undefined) => {
    if (!t) return;
    (t.groups ?? []).forEach(add);
    (t.realm_access?.roles ?? []).forEach(add);
    Object.values(t.resource_access ?? {}).forEach((c) =>
      (c?.roles ?? []).forEach(add),
    );
    // Custom top-level "roles" claim (some mappers put it here).
    if (typeof t.roles === "string") add(t.roles);
    else (t.roles ?? []).forEach(add);
  };
  // Check the access token AND the ID token — some mapper configs only
  // put the claims on one of the two.
  collect(keycloak.tokenParsed as GroupClaims | undefined);
  collect(keycloak.idTokenParsed as GroupClaims | undefined);
  return names;
}

/** True when the signed-in user belongs to an allowed dashboard group. */
export function hasDashboardAccess(): boolean {
  const groups = userGroups();
  return ALLOWED_GROUPS.some((g) => groups.has(normalizeName(g)));
}
